import type { Attribution } from '../utility/unsplash';

export enum Studio {
  home = 'home',
  dashboard = 'story-manager',
  speakers = 'speakers',
  content = 'content',
  creator = 'creator',
  brandKit = 'brand-kit',
  settings = 'settings',
}

export enum ContentTab {
  video_clip = 'Video Clips',
  // PLAT-11877 — second tab, beside Video Clips: the tab bar renders this
  // enum in declaration order. Was 'Transcript & Speakers'; `parseContentTab`
  // still reads the old value from links made before the rename.
  transcript_speakers = 'Transcript & Languages',
  pull_quotes = 'Pull Quotes',
  blog = 'Articles',
  social_posts = 'Social Posts',
  email = 'Email',
  talking_point_studio = 'Talking Points',
  media = 'Media',
  transcript_editor = 'Transcript',
  captions = 'Captions',
}

export enum SidebarOption {
  none = 'none',
  aiProducer = 'ai_producer',
  videoTemplates = 'video_templates',
  media = 'media',
  karaoke = 'karaoke',
  design = 'design',
  editing = 'editing',
  history = 'history',
  videoTemplate = 'video_template',
  speakers = 'speakers',
}

export enum AIProducerCard {
  story = 'story',
  clip = 'clip',
  music = 'music',
  photo = 'photo',
  sound_effect = 'sound_effect',
  retention_edit = 'retention_edit',
  scenes = 'scenes',
  framing = 'framing',
}

export enum MediaCard {
  photo = 'photo',
  music = 'music',
  video = 'video',
}

export enum KaraokeCard {
  templates = 'templates',
  styles = 'styles',
}

export enum DesignCard {
  videoTemplate = 'videoTemplate',
  text = 'text',
  shape = 'shape',
  composition = 'composition',
}

export type PunchListImageGeneration = {
  id: string;
  prompt: string;
  title: string;
  stock_keywords: string;
  dalleImages: Record<'uploadId' | 'uploadUrl', string>[];
};

export type PunchListItemReplacement = {
  type: ImageKey;
  url: string;
  aspectRatio: number;
  alt: string;
  id: string;
};

export type TranscriptContextMenuActions =
  | 'replace'
  | 'hideKaraoke'
  | 'showKaraoke'
  | 'removeText'
  | 'removePhoto'
  | 'addPhoto'
  | 'addOverlayGraphic'
  | 'addOverlayGraphicLoading'
  | 'saveAsClip'
  | 'saveSegmentForLater'
  | 'saveAsPullQuote'
  | 'removeTextLoading'
  | 'removePhotoLoading'
  | 'addPhotoLoading'
  | 'restore'
  | 'cutText'
  | 'pasteTextBefore'
  | 'pasteTextAfter'
  | 'reassignSpeaker'
  | 'undoReassignSpeaker'
  | 'fixTranslation'
  /** PLAT-10833 — translated prose view only: re-dub the selected words. */
  | 'reDubAIVoice'
  | 'addLineBreak'
  | 'removeLineBreak';

/**
 * PLAT-11599 — action → one-sentence reason it is disabled right now. A row
 * listed here renders inert (aria-disabled, muted, click suppressed) with the
 * reason as its hover tooltip. Rows absent from the map are active.
 */
export type TranscriptContextMenuDisabledReasons = Partial<
  Record<TranscriptContextMenuActions, string>
>;

/** What a context-menu action builder hands the menu: the rows, and why any are inert. */
export interface TranscriptContextMenuBuild {
  actions: TranscriptContextMenuActions[];
  disabledReasons: TranscriptContextMenuDisabledReasons;
}

type Img = {
  url: string;
  alt: string;
  id: string;
};
export type ImageKey = 'ai' | 'stock' | 'artifact' | 'quotes';

export type ImageWithType = {
  [key in ImageKey]: Img & {
    type: key;
    width: string;
    height: string;
    fileName?: string;
    alt?: string;
    description?: string;
  } & (key extends 'ai' ? { id: string } : {}) &
    (key extends 'stock' ? { attribution?: Attribution } : {}) &
    (key extends 'quotes' ? { quote?: string } : {});
};

export enum MediaSubType {
  all = 'All Media',
  artifact = 'Artifacts',
  video = 'Videos',
  stock = 'Stock',
  ai = 'AI Generated',
}

export type TalkingPointContent = Record<
  'summary' | 'narrative' | 'impact' | 'quote' | 'takeaway' | 'donation',
  Record<'prompt' | 'content', string>
>;

export type BlogOrEmailContent = {
  id: number;
  title: string;
  content: string;
  username: string;
};

export enum GenericPresetTemplates {
  default = 'default',
  custom = 'custom',
}

export enum KaraokePresetTemplates {
  edge = 'edge',
  classic = 'classic',
  typewriter = 'typewriter',
  blackAndWhite = 'blackAndWhite',
}
